/* eslint-disable @typescript-eslint/camelcase */
import { MssSelection } from '~/reactapp/App/SiteSelectorModal/SiteSelectorModal'
import {
  Circulation,
  IANSReference,
  Section,
  Story,
  StoryTemplate,
  Website,
} from '~/types'
import { set, has, pick, get, xorWith, isEqual } from 'lodash'

import updateLegacyPrimarySite from '~/modules/services/story/updateLegacyPrimarySite'
import { getSectionWebsite } from '~/modules/services/story/getSectionData'
/**
 *
 * @param a - existed/old circulation
 * @param b - new circulation
 */
export function diffCirculations(a: Circulation[], b: Circulation[]) {
  function websiteSectionsChanged(
    sectionsA: IANSReference[],
    sectionsB: IANSReference[]
  ) {
    const uniqueSections = xorWith(sectionsA, sectionsB, isEqual)
    return uniqueSections.length > 0
  }

  function makeCirculationsMap(array: Circulation[]) {
    const map: { [key: string]: Circulation } = {}

    for (const circ of array) {
      map[circ.website_id] = circ
    }

    return map
  }

  const circMapA = makeCirculationsMap(a)
  const circMapB = makeCirculationsMap(b)

  const added: Circulation[] = []
  const changed: Circulation[] = []
  const removed: Circulation[] = []
  const same: Circulation[] = []

  a.forEach((circA) => {
    const circB = circMapB[circA.website_id]

    if (!circB) {
      added.push(circA)
    } else if (circA.website_url && circA.website_url !== circB.website_url) {
      changed.push(circA)
    } else if (
      // eslint-disable-next-line prettier/prettier
      circA.website_primary_section?.referent.id !==
      circB.website_primary_section?.referent.id
    ) {
      changed.push(circA)
    } else if (
      websiteSectionsChanged(
        circA.website_sections || [],
        circB.website_sections || []
      )
    ) {
      changed.push(circA)
    } else {
      same.push(circA)
    }
  })

  b.forEach((circB) => {
    if (!circMapA[circB.website_id]) {
      removed.push(circB)
    }
  })

  return {
    added,
    changed,
    removed,
    same,
  }
}

export function websitesToCirculationDataConverter(
  mssSelection: MssSelection,
  documentId: string
): Circulation[] {
  return Object.entries(mssSelection.websites).map(
    ([websiteId, websiteData]) => {
      const circulation: Circulation = {
        document_id: documentId,
        website_id: websiteId,
        website_sections: mssSelection.taxonomy.sections.filter((section) => {
          return section.referent.website === websiteId
        }),
      }

      if (websiteData.website_url) {
        circulation.website_url = websiteData.website_url
      }

      if (
        websiteData.website_section &&
        Object.keys(websiteData.website_section).length
      ) {
        circulation.website_primary_section = websiteData.website_section

        const isPrimarySectionInAllSections = circulation.website_sections?.some(
          (section) =>
            section.referent.id === websiteData.website_section.referent.id
        )
        if (!isPrimarySectionInAllSections) {
          ;(circulation.website_sections as IANSReference[]).push(
            websiteData.website_section
          )
        }
      } else {
        // Root section selected as primary. MSS does not return an object for this.
        const rootSection = {
          referent: {
            id: '/',
            type: 'section',
            website: websiteId,
          },
          type: 'reference' as const,
        }
        circulation.website_primary_section = rootSection
        ;(circulation.website_sections as IANSReference[]).push(rootSection)
      }

      return circulation
    }
  )
}

export function composeWebsitesFromCirculations(circulations: Circulation[]) {
  return circulations.reduce((accu, circ) => {
    return {
      ...accu,
      [circ.website_id]: {
        website_section:
          (circ.website_sections &&
            circ.website_sections.reduce((accu2, section) => {
              return section.referent.id ===
                circ?.website_primary_section?.referent.id
                ? {
                    ...accu2,
                    ...section,
                  }
                : {
                    ...accu2,
                  }
            }, {})) ||
          {},
        website_url: circ.website_url,
        success: circ.success,
        errorCode: circ.errorCode,
      },
    }
  }, {})
}

export function getCirculationsFromStoryTemplate(
  storyTemplate: StoryTemplate['extra']
) {
  // Need to cover for when there's an empty circulations array, but data on websites
  if (storyTemplate.circulations) {
    // New template or existing template.websites already mapped to circulations
    return storyTemplate.circulations
  } else if (storyTemplate.websites) {
    // Existing template who's websites have not been mapped to circulations.
    // Sections may or may not be denormalized.
    // Root sections may be present as an empty object
    const normalizeSection = (section: IANSReference | Section) => {
      if ('referent' in section) {
        // Normalized section
        return section
      } else {
        // Denormalized section
        return {
          referent: {
            type: 'section',
            id: section._id as string,
            website: section._website,
          },
          type: 'reference' as const,
        }
      }
    }

    const normalizedTaxonomy = {
      ...storyTemplate.taxonomy,
      sections: (storyTemplate?.taxonomy?.sections || []).map((section) =>
        normalizeSection(section)
      ),
    }

    const normalizedWebsites = Object.entries(storyTemplate.websites).reduce(
      (acc, [websiteId, websiteData]) => {
        let sectionReference: IANSReference

        if (websiteData.website_section) {
          sectionReference = normalizeSection(websiteData.website_section)

          if (!sectionReference.referent.website) {
            sectionReference.referent.website = websiteId
          }
        } else {
          // Root section as empty object
          sectionReference = {
            referent: {
              id: '/',
              type: 'section',
              website: websiteId,
            },
            type: 'reference' as const,
          }
        }

        return {
          ...acc,
          [websiteId]: {
            ...websiteData,
            website_section: sectionReference,
          },
        }
      },
      {}
    )

    const circulations = websitesToCirculationDataConverter(
      {
        taxonomy: normalizedTaxonomy,
        websites: normalizedWebsites,
      },
      ''
    )

    return circulations
  } else {
    return []
  }
}

export function composeTaxonomyFromCirculations(
  circulations: Circulation[],
  canonicalWebsite: string
) {
  const taxonomy: Story['taxonomy'] = {
    sections: [],
    primary_section: undefined,
  }

  for (const circulation of circulations) {
    if (circulation.website_sections && taxonomy.sections) {
      taxonomy.sections.push(...circulation.website_sections)
    }

    if (circulation.website_id === canonicalWebsite) {
      taxonomy.primary_section = circulation.website_primary_section
    }
  }

  return taxonomy
}

export function normalizeAnsFromCirculations(
  story: Story,
  reduxCirculations: Circulation[],
  selectedPublishDate: Date
) {
  if (!story) {
    return null
  }

  const emptyTaxonomy = {
    tags: [],
    seo_keywords: [],
    additional_properties: {},
  }

  return {
    ...story,
    taxonomy: {
      ...(story?.taxonomy || emptyTaxonomy),
      tags: [...(story?.taxonomy?.tags || [])],
      seo_keywords: [...(story?.taxonomy?.seo_keywords || [])],
      ...composeTaxonomyFromCirculations(
        reduxCirculations || [],
        (story?.canonical_website || '') as string
      ),
    },
    websites: composeWebsitesFromCirculations(reduxCirculations || []),
    // Pass the scheduled publish date to use in URL generation
    publish_date: selectedPublishDate?.toISOString(),
  }
}

// From websiteSelection.utils
export function updateStory(story, websites, sections, defaultWebsite) {
  set(story, 'websites', websites)
  set(story, 'taxonomy.sections', sections)

  if (story.canonical_website) {
    if (websites[story.canonical_website]) {
      if (websites[story.canonical_website].website_section) {
        // Sync primary_section with the primary website's website_section
        story.taxonomy.primary_section =
          websites[story.canonical_website].website_section
      } else {
        // Remove primary_section since website_section is removed from the primary website
        delete story.taxonomy.primary_section
      }
    } else {
      // Remove canonical_website and primary_section since the original primary website has been removed
      delete story.canonical_website
      delete story.taxonomy.primary_section
    }
  }

  if (defaultWebsite) {
    // Handle legacy taxonomy.primary_site
    updateLegacyPrimarySite(story, defaultWebsite._id)

    // Handle legacy taxonomy.sites
    story.taxonomy.sites = story.taxonomy.sections
      .filter((section) => getSectionWebsite(section) === defaultWebsite._id)
      .map((section) => {
        return has(section, 'referent')
          ? {
              type: 'reference',
              referent: {
                ...pick(section.referent, ['id', 'provider']),
                type: 'site',
              },
            }
          : {
              ...section,
              type: 'site',
            }
      })
  } else {
    delete story.taxonomy.primary_site
    story.taxonomy.sites = []
  }
}

export function filterWebsites(
  websites: { [websiteId: string]: Website },
  permissions,
  hierarchy
) {
  // Any falsy values should become default
  hierarchy = hierarchy || 'default'

  const filteredSites = Object.values(websites || {}).map((website) => {
    return {
      ...website,
      hierarchy,
    }
  })

  const publishPerm = get(permissions, 'publish', {})
  const circulatePerm = get(permissions, 'circulate', {})

  if (publishPerm.all || circulatePerm.all) {
    return filteredSites
  } else {
    return filteredSites.filter((website) => {
      return (
        !!publishPerm[website._id.toLowerCase()] ||
        !!circulatePerm[website._id.toLowerCase()]
      )
    })
  }
}
